/**
 * The league calendar the app starts from, in one place.
 *
 * It used to live in `MemoryStore`'s constructor, which was fine while there was one store and
 * became a bug the moment there were two: the deployed app would have come up with no deadlines
 * at all — no keeper date on the home screen, no lock on the declaration form — because the seed
 * was in the half of the code the deployed app does not run.
 *
 * ISO INSTANTS, ALWAYS. The rulebook says "5:00pm" and never says where; the league is spread
 * across Mountain and Eastern; a wall-clock string would mean something different to Tim than to
 * everyone else. These are the announced Eastern times converted once, and rendered back into
 * both zones by `src/domain/deadlines.ts`.
 *
 * `updatedAt` is the date the dates were ANNOUNCED, not the date this file was edited, and that
 * is load-bearing. Both stores treat a saved settings document as authoritative only if it is
 * newer than the seed — which is what lets a commissioner's correction survive a redeploy while a
 * stale copy in somebody's browser loses to a corrected build.
 */

import type { LeagueSettings } from './types.js';

export const SEED_SEASON = 2026;

export const SEED_SETTINGS_2026: LeagueSettings = {
  season: 2026,
  /** Monday 31 August, noon ET. */
  keeperDeadline: '2026-08-31T16:00:00.000Z',
  declarationsLocked: false,
  /**
   * Monday 31 August, FIVE PM ET — five hours after the keeper lock, not with it.
   *
   * They shared a moment until 26 August and should not have: since 2020-II-1 the lottery hands
   * out the order in which GMs CHOOSE a slot, and the draw runs at the Competition Committee on
   * the 26th. So a GM ranks slots knowing his turn, and he cannot know what his keepers cost until
   * slates lock. Noon for the slates, 5pm for the ranking that depends on them, the same afternoon.
   * The third-place option (2018-II-5) closes at the same moment, which is what makes 2020-II-1 §7
   * able to resolve everything at once.
   */
  preferenceDeadline: '2026-08-31T21:00:00.000Z',
  preferencesLocked: false,
  /**
   * Tuesday 11 August, 11:59pm ET — a fortnight before the meeting rather than the night before,
   * which is the drift the written deadline exists to stop. Already passed.
   */
  proposalDeadline: '2026-08-12T03:59:00.000Z',
  /** Wednesday 26 August, 8:00pm ET. */
  committeeDate: '2026-08-27T00:00:00.000Z',
  /** Tuesday 8 September, 8:00pm ET. */
  draftDate: '2026-09-09T00:00:00.000Z',
  /*
   * BUMPED WITH THE CORRECTION ABOVE. `MemoryStore.load` keeps a saved settings document only when
   * it is NEWER than the seed — the rule that stops a browser's stale copy of the calendar
   * loading over a redeploy. A corrected date that kept the old stamp would lose to every browser
   * that had ever cached the wrong one.
   *
   * This is the offline and preview store only. The live value is in Firestore and is set on
   * Admin › Deadlines.
   */
  updatedAt: '2026-08-26T22:10:00.000Z',
  updatedBy: 'TIM',
};
